import { useState } from 'react';
import { Alert, Button, Card, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '@prooffleet/client';
import { currentLocale } from '../i18n/index.js';
import { useSession } from '@prooffleet/client';

interface PolicyPayload {
  version: number;
  texts: Record<string, { title: string; body: string; textHash: string }>;
}

/**
 * Экран регламента. Подтверждается не нажатие кнопки, а нажатие под конкретным
 * текстом: вместе с текстом сервер отдаёт его хеш, и он уходит обратно с подписью.
 */
export function Policy(): React.JSX.Element {
  const { t } = useTranslation();
  const setAcknowledged = useSession((s) => s.setPolicyAcknowledged);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['policy'],
    queryFn: () => api<PolicyPayload>('/policy'),
  });

  if (isLoading || data === undefined) {
    return (
      <div className="pf-scroll">
        <Text>{t('common.loading')}</Text>
      </div>
    );
  }

  const locale = currentLocale();
  const text = data.texts[locale] ?? data.texts['en'] ?? Object.values(data.texts)[0];

  const accept = async (): Promise<void> => {
    if (text === undefined) return;
    setBusy(true);
    setError(null);
    try {
      await api('/policy/acknowledge', {
        method: 'POST',
        body: { version: data.version, locale, textHash: text.textHash },
      });
      setAcknowledged(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409 ? t('policy.changed') : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="md" maw={640} mx="auto" w="100%">
          <div>
            <Title order={1}>{t('policy.title')}</Title>
            <Text c="dimmed">{t('policy.version', { version: data.version })}</Text>
          </div>

          <Alert color="blue" variant="light">
            {t('policy.required')}
          </Alert>

          <Card>
            <Title order={3} mb="sm">
              {text?.title}
            </Title>
            <ScrollArea.Autosize mah={360}>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{text?.body}</Text>
            </ScrollArea.Autosize>
          </Card>

          {error !== null && <Alert color="red">{error}</Alert>}
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Button fullWidth loading={busy} onClick={() => void accept()}>
          {busy ? t('policy.accepting') : t('policy.accept')}
        </Button>
      </div>
    </div>
  );
}
